import * as THREE from 'three';
import { ModuleType } from '../../../constants/enum';
import { anchorNeedsBox, getLocalFrameBox } from './AnchorResolver';

/**
 * Bộ phận được tạo riêng, không phải module: mặt đá (countertop) và ốp tường
 * (backsplash). Chúng bị TẠO LẠI hoàn toàn mỗi lần module đổi, nên DIM không neo
 * vào mesh mà neo vào MODULE SỞ HỮU + loại bộ phận:
 *   { instanceId: <module>, part: 'countertop' | 'backsplash', key | rel }
 * Mỗi frame tra lại mesh hiện tại của bộ phận đó (PartIndex).
 */
export const PART = {
  COUNTERTOP: 'countertop',
  BACKSPLASH: 'backsplash',
};

const PART_BY_TYPE = {
  [ModuleType.COUNTERTOP]: PART.COUNTERTOP,
  [ModuleType.BACKSPLASH]: PART.BACKSPLASH,
};

/** Loại bộ phận ứng với userData.type của một đối tượng (hoặc null). */
export const partOfObject = object =>
  PART_BY_TYPE[object?.userData?.type] ?? null;

const isVisibleDeep = object => {
  for (let o = object; o; o = o.parent) {
    if (o.visible === false) return false;
  }
  return true;
};

/** Các mesh nhìn thấy được của một plane (Mesh hoặc Group), theo từng bộ phận. */
const meshesByPart = plane => {
  const result = {};
  plane?.traverse?.(o => {
    if (!o.isMesh || !o.geometry || !isVisibleDeep(o)) return;
    const part = partOfObject(o) ?? partOfObject(o.parent);
    if (!part) return;
    (result[part] ??= []).push(o);
  });
  return result;
};

const defaultInstanceId = group => group?.userData?.instanceId;

/**
 * Dựng chỉ mục các bộ phận từ danh sách plane của CreateGroupPlane.
 *
 * @param {Iterable<{plane: THREE.Object3D, modules?: THREE.Group[]}>} entries
 * @returns {{
 *   parts: Map<string, {instanceId, part, group, meshes: THREE.Mesh[]}>,
 *   targets: {instanceId, part, group, meshes}[],
 *   getPartMeshes: (instanceId: string, part: string) => THREE.Mesh[] | null
 * }}
 *   - parts: theo từng module và loại bộ phận, gộp MỌI plane chứa module đó
 *   - targets: mỗi (plane, loại) đúng một mục, chủ sở hữu = module có instanceId
 *     nhỏ nhất (ổn định giữa các lần tạo lại); dùng cho Auto DIM
 */
export const buildPartIndex = (entries, instanceIdOf = defaultInstanceId) => {
  const parts = new Map();
  const targets = [];
  const seenTargets = new Set();

  for (const { plane, modules } of entries ?? []) {
    const owners = (modules ?? [])
      .filter(g => instanceIdOf(g))
      .sort((a, b) => (instanceIdOf(a) < instanceIdOf(b) ? -1 : 1));
    if (!owners.length) continue;
    const byPart = meshesByPart(plane);

    Object.entries(byPart).forEach(([part, meshes]) => {
      owners.forEach(group => {
        const key = `${instanceIdOf(group)}:${part}`;
        let entry = parts.get(key);
        if (!entry) {
          entry = { instanceId: instanceIdOf(group), part, group, meshes: [] };
          parts.set(key, entry);
        }
        meshes.forEach(m => {
          if (!entry.meshes.includes(m)) entry.meshes.push(m);
        });
      });
      const ownerId = instanceIdOf(owners[0]);
      const key = `${ownerId}:${part}`;
      if (!seenTargets.has(key)) {
        seenTargets.add(key);
        targets.push(parts.get(key));
      }
    });
  }

  return {
    parts,
    targets,
    getPartMeshes: (instanceId, part) =>
      parts.get(`${instanceId}:${part}`)?.meshes ?? null,
  };
};

/**
 * Bbox của các mesh bộ phận trong hệ LOCAL của module sở hữu (AABB trong hệ của
 * module, nên chiều rộng/sâu/cao khớp quy ước của module: Z rộng, X sâu, Y cao).
 */
export const getPartBox = (group, meshes) => {
  if (!group || !meshes?.length) return null;
  group.updateWorldMatrix(true, false);
  const inverse = group.matrixWorld.clone().invert();
  const toLocal = new THREE.Matrix4();
  const v = new THREE.Vector3();
  const box = new THREE.Box3();
  meshes.forEach(mesh => {
    if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
    const gb = mesh.geometry.boundingBox;
    if (gb.isEmpty()) return;
    mesh.updateWorldMatrix(true, false);
    toLocal.multiplyMatrices(inverse, mesh.matrixWorld);
    for (const x of [gb.min.x, gb.max.x]) {
      for (const y of [gb.min.y, gb.max.y]) {
        for (const z of [gb.min.z, gb.max.z]) {
          box.expandByPoint(v.set(x, y, z).applyMatrix4(toLocal));
        }
      }
    }
  });
  return box.isEmpty() ? null : box;
};

/**
 * Bbox dùng để giải một anchor: bbox của bộ phận nếu anchor có `part`, bbox khung
 * của module nếu cần (key / rel), null nếu anchor tự đủ (local).
 * `opts.getPartMeshes` bắt buộc để giải anchor có `part`.
 */
export const boxForAnchor = (group, anchor, opts) => {
  if (anchor?.part) {
    return getPartBox(
      group,
      opts?.getPartMeshes?.(anchor.instanceId, anchor.part)
    );
  }
  return anchorNeedsBox(anchor) ? getLocalFrameBox(group, opts) : null;
};

/** Phân loại module cho preset hiển thị: 'upper' | 'lower' | 'overall'. */
export const moduleKindOf = group => {
  const type = String(
    group?.userData?.moduleData?.moduleType ?? group?.userData?.moduleType ?? ''
  ).toLowerCase();
  if (type.includes('upper') || type.includes('ceiling')) return 'upper';
  if (type.includes('lower')) return 'lower';
  return 'overall';
};
